/**
 * lib/campaignAudienceGate.js — v200.46 (2026-08-13)
 *
 * Single source of truth for the "zero-audience card is disabled" rule
 * established in v200.42 (Aug 9). Both the AI recommender and the
 * customer-lab suggestions screen import this — one function, one
 * behaviour. If the rule changes, it changes in ONE place.
 *
 * Rule:
 *   A campaign card whose forecast audience is 0 must have:
 *     · its "launch/execute now" button disabled
 *     · its forecast metrics displayed as "—" (not a derived number)
 *     · its border/opacity dimmed to signal inactivity
 *
 * The v200.42 regression fix (2026-08-09) established this. Any
 * new "launch now" surface MUST call `isZeroAudience(size)` before
 * enabling its button, and must display "—" for forecasts when it
 * returns true.
 */

/**
 * @param  {number|string|null|undefined} audienceSize
 * @return {boolean} true iff the audience is exactly zero (or missing)
 */
export function isZeroAudience(audienceSize) {
  return (Number(audienceSize) || 0) === 0;
}

/**
 * Aggregate variant: given an array of items each carrying an audience
 * size, returns true iff EVERY item is zero-audience. Used by the
 * "execute all" bar in CustomerLab — the bar is disabled only when
 * there is literally nothing to send.
 *
 * @param {Array<{estimated_recipients?: number, audience_size?: number}>} items
 * @return {boolean}
 */
export function allZeroAudience(items) {
  if (!Array.isArray(items) || items.length === 0) return true;
  return items.every(it =>
    isZeroAudience(it?.estimated_recipients ?? it?.audience_size ?? 0),
  );
}

/**
 * The placeholder string to display in forecast slots when the
 * audience is zero. Kept as a constant so guards can grep for the
 * exact literal.
 */
export const ZERO_AUDIENCE_PLACEHOLDER = "—";
